import { CiReport, Integer, generatePath } from '@cibo/core'
import { Loading } from '@cibo/profile'
import Check from '@mui/icons-material/Check'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  Container,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { ascend, pipe, prop, propEq, reject, sort } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DataGridPro, ResponsiveDialog } from '../../../../../consts/Theme'
import { useInventory, useSetInventoryBasePeriod } from '../../../../../features/Inventories'
import { HistoricalCarbonIntensity } from '../../../../../features/Inventories/components/HistoricalCarbonIntensity'
import { SCOPE_THREE_ROUTES } from '../../../../../navigation/types/scopeThreeRoutes'
import { WaitingPage } from '../../Root/WaitingPage'

interface BasePeriodRow {
  year: number
  soy: number
  corn: number
}

export const SelectBasePeriod = () => {
  const { inventoryId } = useParams()
  const [refetchInterval, setRefetchInterval] = useState<number | false>(1000)
  const { data: inventory, isLoading, dataUpdatedAt } = useInventory(inventoryId, {
    refetchInterval,
  })
  const setBasePeriod = useSetInventoryBasePeriod(inventoryId)

  const { t } = useTranslation('impact/SelectBasePeriod')
  const { palette } = useTheme()
  const navigate = useNavigate()

  const [commodity] = useState('Corn')
  const [basePeriodDialog, setBasePeriodDialog] = useState<{ open: boolean }>({ open: false })
  const [selectionModel, setSelectionModel] = useState<number[]>(inventory?.item?.data.years || [])
  const [confirmInput, setConfirmInput] = useState<string>('')
  const [showError, setShowError] = useState<boolean>(false)

  useEffect(() => {
    setConfirmInput('')
  }, [basePeriodDialog.open])

  useEffect(() => {
    inventory?.item.data.ciJobResult?.status === 'complete' && setRefetchInterval(false)
  }, [dataUpdatedAt])

  const hasCorn = !!inventory?.item.data.ciJobResult?.ciReport?.find(a => !!a.corn)
  const hasSoy = !!inventory?.item.data.ciJobResult?.ciReport?.find(a => !!a.soybeans)

  const columns = useMemo(() => {
    const cornIfPresent = hasCorn
      ? [
          {
            field: 'corn',
            headerName: t('corn'),
            flex: 1,
            renderCell: ({ value }: { value: string }) => t('kgCarbon', { value }),
          },
        ]
      : []
    const soyIfPresent = hasSoy
      ? [
          {
            field: 'soybeans',
            headerName: t('soy'),
            flex: 1,
            renderCell: ({ value }: { value: string }) => t('kgCarbon', { value }),
          },
        ]
      : []
    return [
      {
        field: 'year',
        headerName: t('year'),
        flex: 1,
      },
      ...cornIfPresent,
      ...soyIfPresent,
    ]
  }, [dataUpdatedAt])

  if (isLoading) return <Loading large />

  const rows = pipe<CiReport, CiReport, CiReport>(
    reject(propEq('year', 2024)),
    sort(ascend(prop('year')))
  )(inventory?.item.data.ciJobResult?.ciReport || [])

  if (inventory?.item.data.ciJobResult?.status === 'complete' && !!rows) {
    const isSelectable = !inventory.item.data.years || inventory.item.data.years?.length === 0
    return (
      <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Grid container spacing={3} paddingX={3} paddingBottom={3}>
          <Grid item sm={12} md={4}>
            <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
              <Grid container padding={2} spacing={2}>
                <Grid item style={{ maxWidth: '40px' }}>
                  <HelpOutlineIcon htmlColor={palette.text.secondary} />
                </Grid>
                <Grid item style={{ maxWidth: 'calc(100% - 56px)' }}>
                  <Stack spacing={2}>
                    <Typography variant="h6">{t('setupTip')}</Typography>
                    <Typography>{t('setupTipContent')}</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item sm={12} md={8}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
                <Stack padding={3} spacing={1}>
                  <Typography>{t('historicalCarbonIntensity')}</Typography>
                  <Typography color="text.secondary">{t('historicalSubtitle')}</Typography>

                  <HistoricalCarbonIntensity reportData={rows} commodity={commodity} height={200} />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
                <Stack padding={3} spacing={1}>
                  <Typography>{t('basePeriod')}</Typography>
                  <Typography color="text.secondary">{t('basePeriodSubtitle')}</Typography>
                  <Box style={{ height: ((rows?.length || 5) + 1) * 52 + 4 }}>
                    <DataGridPro<BasePeriodRow>
                      sx={{
                        '.MuiDataGrid-row--lastVisible': {
                          '.MuiDataGrid-cell': {
                            borderBottom: 'none',
                          },
                        },
                      }}
                      selectionModel={selectionModel}
                      onSelectionModelChange={
                        isSelectable
                          ? selectionModel => setSelectionModel(selectionModel as Integer[])
                          : undefined
                      }
                      disableColumnMenu
                      checkboxSelection={isSelectable}
                      hideFooter
                      rows={rows || []}
                      getRowId={i => i.year}
                      columns={columns as GridColumns<BasePeriodRow>}
                    />
                  </Box>
                  {inventory.item.data.years && inventory.item.data.years?.length > 0 && (
                    <Alert severity="warning">{t('basePeriodAlreadySet')}</Alert>
                  )}
                </Stack>
              </Paper>
              <LoadingButton
                startIcon={<Check />}
                variant="contained"
                sx={{ alignSelf: 'flex-end' }}
                disabled={
                  selectionModel.length === 0 ||
                  (inventory.item.data.years && inventory.item.data.years?.length > 0)
                }
                onClick={() => setBasePeriodDialog({ open: true })}
              >
                {t('saveInventoryBoundary')}
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
        <ResponsiveDialog
          open={basePeriodDialog.open}
          onClose={() => {
            setBasePeriodDialog({ open: false })
          }}
        >
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1">{t('setBasePeriodTitle')}</Typography>
              <Alert severity="warning">{t('setBasePeriodMessage')}</Alert>
              <TextField
                error={showError}
                helperText={showError && t('inputError', { name: inventory?.item.name })}
                value={confirmInput}
                onBlur={() => setShowError(confirmInput !== inventory?.item.name)}
                onChange={event => {
                  setShowError(false)
                  setConfirmInput(event.target.value)
                }}
                InputLabelProps={{ shrink: true }}
                label={t('inputLabel', { name: inventory?.item.name })}
                placeholder={t('inputPlaceholder')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setBasePeriodDialog({ open: false })
                setConfirmInput('')
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              color="warning"
              disabled={confirmInput !== inventory?.item.name}
              onClick={() => {
                setBasePeriod.mutateAsync({ years: selectionModel }).then(a => {
                  setBasePeriodDialog({ open: false })
                  navigate(generatePath(SCOPE_THREE_ROUTES.DETAIL, { inventoryId }))
                })
              }}
              loading={setBasePeriod.isLoading}
            >
              {t('confirmBasePeriod')}
            </LoadingButton>
          </DialogActions>
        </ResponsiveDialog>
      </Container>
    )
  }
  return <WaitingPage />
}
